import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { MyContextProvider } from "../App";
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardCards from './Dashboard/DashboardCards';
import { GlobalLoading, showLoading } from 'react-global-loading';

// Import role-based components
import ASICard from './Dashboard/ASICard';
import IIPCard from './Dashboard/IIPCard';
import PriceWagesCard from './Dashboard/PriceWagesCard';
import TopDist from './Dashboard/TopDist';
import MonthlyGrowthRateChart from './Dashboard/MonthlyGrowthRateChart';
// Import role utilities
import { getDashboardComponents, getUserRoleType } from '../utils/roleUtils';

// Import other components
import StatCard from './Dashboard/StatCard';
import MonthlyTrend from './Dashboard/MonthlyTrend';
// import { API_URL, getUserClaimsForUi } from '../Constant';
import { API_URL, getUserClaimsForUi } from "../Constant";

function getTopDistUserType(userRoleType) {
  if (userRoleType === 'ADMIN USER' || userRoleType === 'SUPER ADMIN USER') return 'HQAdmin';
  if (userRoleType === 'ASI USER') return 'HQASI';
  if (userRoleType === 'IIP USER') return 'IIP';
  if (userRoleType === 'PRICE & WAGES USER') return 'Price';
  return '';
}

function resolveDecodedToken(loginCtx) {
  const ui = loginCtx?.userInfo;
  if (ui?.id && typeof ui.id === 'object') {
    return { id: ui.id };
  }
  const t = loginCtx?.token;
  if (t && typeof t === 'string' && t.trim() && t !== 'null') {
    try {
      return jwtDecode(t);
    } catch (_) {
      /* ignore */
    }
  }
  return getUserClaimsForUi();
}

function Dashboard() {
  const loginCtx = useContext(MyContextProvider);
  const decodedToken = useMemo(() => resolveDecodedToken(loginCtx), [loginCtx?.userInfo, loginCtx?.token]);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardComponents, setDashboardComponents] = useState({
    showASI: false,
    showIIP: false,
    showPriceWages: false,
    showAgriculture: false,
    showAll: false
  });
  const [userRoleType, setUserRoleType] = useState('');
  const [priceSummaryData, setPriceSummaryData] = useState(null);
  const [priceYear, setPriceYear] = useState('2025');
  const [priceMonth, setPriceMonth] = useState(() => {
    const now = new Date();
    const monthNumber = (now.getMonth() + 1).toString().padStart(2, '0');
    return monthNumber;
  });
  const [asiCardData, setAsiCardData] = useState(null);
  const [iipCardData, setIipCardData] = useState(null);
  const [dslStatusSummary, setDslStatusSummary] = useState(null);
  const [selectedASIYear, setSelectedASIYear] = useState(null);
  const priceDataFetchRef = useRef({ isFetching: false, lastParams: null });
  const dashboardDataFetchRef = useRef({ isFetching: false, hasFetched: false });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const determineComponents = async () => {
      if (!decodedToken?.id) return;
      try {
        const components = await getDashboardComponents(decodedToken);
        const roleType = getUserRoleType(decodedToken);

        setDashboardComponents(components);
        setUserRoleType(roleType);
      } catch (error) {
        console.error('Error determining dashboard components:', error);
      }
    };

    determineComponents();
  }, [decodedToken]);

  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (dashboardDataFetchRef.current.isFetching) {
      return;
    }

    const fetchDashboardData = async () => {
      dashboardDataFetchRef.current.isFetching = true;
      setLoading(true);
      showLoading(true);
      try {
        let year;
        if (selectedASIYear) {
          // If ASI year is selected, use its starting year
          year = parseInt(selectedASIYear.split('-')[0]);
        } else {
          // Calculate current financial year and go 1 year back
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth();

          const currentFinancialYearStart = currentMonth < 3 ? currentYear - 1 : currentYear;

          // Go 1 year back from current financial year
          year = currentFinancialYearStart - 1;
        }
        const response = await axios.get(`${API_URL}api/totalCountDisDivGvadeshboard`, {
          params: {
            year: year
          }
        });
        if (response.data.status) {
          setApiData(response.data.data);
        }
      } catch (error) {
        setApiData(null);
      } finally {
        setLoading(false);
        dashboardDataFetchRef.current.isFetching = false;
      }
    };
    fetchDashboardData();
  }, [selectedASIYear]);

  // Lazy load Price API - no loader
  useEffect(() => {
    // Create a unique key for the request parameters
    const requestKey = `${priceYear}-${priceMonth}`;

    // Prevent duplicate calls (especially from React StrictMode)
    if (priceDataFetchRef.current.isFetching && priceDataFetchRef.current.lastParams === requestKey) {
      return;
    }

    // Check if same request was just made
    if (priceDataFetchRef.current.lastParams === requestKey && !priceDataFetchRef.current.isFetching) {
      return;
    }

    async function fetchPriceData() {
      priceDataFetchRef.current.isFetching = true;
      priceDataFetchRef.current.lastParams = requestKey;
      // No loader for this API - lazy loading
      try {
        const res = await axios.get(`${API_URL}api/dashboard/price/comprehensive?year=${priceYear}&month=${priceMonth}`);
        if (res.data.status && res.data.data) {
          setPriceSummaryData(res.data.data);
        } else {
          setPriceSummaryData(null);
        }
      } catch {
        setPriceSummaryData(null);
      } finally {
        priceDataFetchRef.current.isFetching = false;
      }
    }
    fetchPriceData();
  }, [priceYear, priceMonth]);

  // Hide loader only when main dashboard API call is done
  useEffect(() => {
    if (!loading) {
      showLoading(false);
    }
  }, [loading]);

  // Base stats data - Total Industries excluded for ASI USER
  const baseStatsData = [
    {
      title: "Total Divisions",
      value: apiData?.getTotaldivisionCont?.[0]?.total_divisions || "19",
      change: "0%",
      icon: "🏢",
      color: "warning",
      bg: "linear-gradient(135deg, #ff6b35 0%, #ff4500 100%)"
    },
    {
      title: "Total District",
      value: (typeof apiData?.getUnitCodesdata?.[0]?.total_districts === 'number')
        ? apiData.getUnitCodesdata[0].total_districts - 1
        : apiData?.getUnitCodesdata?.[0]?.total_districts,
      change: "",
      icon: "🗺️",
      color: "warning",
      bg: "linear-gradient(135deg, #ff7f50 0%, #ff6347 100%)"
    },
    {
      title: userRoleType === 'ASI USER' && selectedASIYear
        ? `Total DSL (${selectedASIYear.split('-')[0]})`
        : apiData?.getTotalcountlastYearbase?.[0]
          ? `Total DSL (${apiData.getTotalcountlastYearbase[0].year})`
          : "Total DSL",
      value: (() => {
        // For ASI USER, prioritize asiCardData
        if (userRoleType === 'ASI USER' && asiCardData?.getTotalcountlastYearbase?.[0]?.total_dsl_count !== undefined && asiCardData?.getTotalcountlastYearbase?.[0]?.total_dsl_count !== null) {
          return asiCardData.getTotalcountlastYearbase[0].total_dsl_count;
        }
        // Fallback to apiData
        if (apiData?.getTotalcountlastYearbase?.[0]?.total_dsl_count !== undefined && apiData?.getTotalcountlastYearbase?.[0]?.total_dsl_count !== null) {
          return apiData.getTotalcountlastYearbase[0].total_dsl_count;
        }
        return "";
      })(),
      change: "+5%",
      icon: "📊",
      color: "warning",
      bg: "linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)"
    }
  ];

  // DSL Stats Cards for ASI USER (HQASI) - excluding Total DSL as it's already in baseStatsData
  const dslStatsData = userRoleType === 'ASI USER' && dslStatusSummary ? [
    {
      title: "Pending DSL",
      value: dslStatusSummary?.totalPending || dslStatusSummary?.total_pending || "0",
      change: "",
      icon: "⏳",
      color: "warning",
      bg: "linear-gradient(135deg, #ff8c42 0%, #ff6b1a 100%)"
    },
    {
      title: "Submitted DSL",
      value: dslStatusSummary?.totalSubmitted || dslStatusSummary?.total_submitted || "0",
      change: "",
      icon: "📤",
      color: "warning",
      bg: "linear-gradient(135deg, #ff9f5a 0%, #ff8555 100%)"
    },
    {
      title: "Accepted DSL",
      value: dslStatusSummary?.totalAccepted || dslStatusSummary?.total_accepted || "0",
      change: "",
      icon: "✅",
      color: "success",
      bg: "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
    },
    {
      title: "Rejected DSL",
      value: dslStatusSummary?.totalRejected || dslStatusSummary?.total_rejected || "0",
      change: "",
      icon: "❌",
      color: "danger",
      bg: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
    }
  ] : [];

  // For ASI USER: exclude Total Industries, add DSL stats
  // For others: include Total Industries
  const statsData = userRoleType === 'ASI USER'
    ? [...baseStatsData, ...dslStatsData]
    : [
      ...baseStatsData,
      {
        title: "Total Industries",
        value: apiData?.getTotalIndustialCont?.[0]?.total_count || "2,847",
        change: "+12%",
        icon: "🏭",
        color: "warning",
        bg: "linear-gradient(135deg, #ff8c00 0%, #ff6b35 100%)"
      }
    ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const gradientText = {
    background: 'linear-gradient(135deg, #ff8c00 0%, #ff4500 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  };

  // Determine if only one card should be full width
  const visibleCards = [dashboardComponents.showASI, dashboardComponents.showIIP, dashboardComponents.showPriceWages, dashboardComponents.showAgriculture].filter(Boolean).length;
  const fullWidth = visibleCards === 1;

  return (
    <>
      <GlobalLoading />
      <div className="container-fluid p-4">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h3 mb-0" style={gradientText}>
                  <i className="fa fa-line-chart me-2"></i>
                  Uttar Pradesh Statistics Dashboard
                </h1>
                {userRoleType && (
                  <p className="text-muted mb-0">
                    Welcome, {userRoleType}
                  </p>
                )}
              </div>
              <div className="text-end">
                <div className="text-muted small">
                  {currentTime.toLocaleDateString('en-IN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                <div className="h5 mb-0" style={{ color: '#ff8c00' }}>
                  {currentTime.toLocaleTimeString('en-IN')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards - Only for HQAdmin, SuperAdmin and ASI users */}
        {(userRoleType === 'ADMIN USER' || userRoleType === 'SUPER ADMIN USER' || userRoleType === 'ASI USER') && (
          <div className="row mb-4">
            {!loading && statsData.map((stat, index) => (
              <StatCard key={index} stat={stat} />
            ))}
          </div>
        )}

        {/* Main Module Cards - Role-based rendering */}
        <div className="row mb-4">
          {dashboardComponents.showASI && (
            <ASICard
              handleCardClick={handleCardClick}
              fullWidth={false}
              onDataChange={setAsiCardData}
              onDSLStatusChange={setDslStatusSummary}
              onYearChange={setSelectedASIYear}
            />
          )}
          {dashboardComponents.showIIP && (
            <IIPCard handleCardClick={handleCardClick} fullWidth={false} onDataChange={setIipCardData} />
          )}
          {dashboardComponents.showPriceWages && (
            <PriceWagesCard handleCardClick={handleCardClick} fullWidth={false} summaryData={priceSummaryData} priceYear={priceYear} setPriceYear={setPriceYear} priceMonth={priceMonth} setPriceMonth={setPriceMonth} />
          )}
          {/* Always show TopDist for all users */}
          <TopDist userType={getTopDistUserType(userRoleType)} fullWidth={false} />

          {dashboardComponents.showIIP && (
            <MonthlyGrowthRateChart ullWidth={false} />
          )}
        </div>

        {/* Charts and Analytics Section */}
        {/* <div className="row mb-4">
          <MonthlyTrend chartData={chartData} />
        </div> */}

        {/* <DashboardCards userRoleType={userRoleType} priceSummaryData={priceSummaryData} asiCardData={asiCardData} iipCardData={iipCardData} /> */}
      </div>
    </>
  );
}

export default Dashboard;
